import React, { useState, useMemo, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { InputText } from 'primereact/inputtext';

import { useGetAllModelsQuery } from '../../../api/endpoints/model';
import { useGetAllBrandsQuery } from '../../../api/endpoints/brand';
import { useLanguage } from '../../../contexts/LanguageContext';

import BrandDropdown from '../../../components/common/BrandDropdown';
import ModelDropdown from '../../../components/common/ModelDropdown';

const ModelPage = () => {
    const { th } = useLanguage();

    // tablo state
    const [lazyParams, setLazyParams] = useState({ first: 0, rows: 10, page: 1 });
    const [searchText, setSearchText] = useState('');
    const [activeApiFilters, setActiveApiFilters] = useState({
        Brand: null,
        ModelName: null,
        ModelCode: null,
        BrandName: null
    });

    // filtre UI state
    const [filterOpen, setFilterOpen] = useState(false);
    const [brandValue, setBrandValue] = useState(null); // { id, brandShort, displayValue }
    const [productGroupValue, setProductGroupValue] = useState(''); // ModelCode (Input)
    const [modelValue, setModelValue] = useState(null); // { id, name, displayValue }

    // API çağrısı
    const { data: apiResult, isLoading: loadingModels } = useGetAllModelsQuery({
        PageNumber: lazyParams.page,
        PageSize: lazyParams.rows,
        SearchText: searchText,
        Brand: activeApiFilters.Brand ?? null,
        ModelName: activeApiFilters.ModelName ?? null,
        ModelCode: activeApiFilters.ModelCode ?? null,
        BrandName: activeApiFilters.BrandName ?? null
    });

    const models = useMemo(() => apiResult || { items: [], totalCount: 0 }, [apiResult]);

    // tablo local sort
    const [displayedModels, setDisplayedModels] = useState([]);
    const [sortInfo, setSortInfo] = useState({ sortField: null, sortOrder: null });

    useEffect(() => {
        if (models && models.items) {
            let items = [...models.items];
            if (sortInfo.sortField && sortInfo.sortOrder !== null) {
                items.sort((a, b) => {
                    const va = a[sortInfo.sortField];
                    const vb = b[sortInfo.sortField];
                    let cmp = 0;
                    if (va == null && vb != null) cmp = -1;
                    else if (va != null && vb == null) cmp = 1;
                    else if (va == null && vb == null) cmp = 0;
                    else if (typeof va === 'string' && typeof vb === 'string') cmp = va.localeCompare(vb, undefined, { sensitivity: 'base' });
                    else if (typeof va === 'number' && typeof vb === 'number') cmp = va - vb;
                    else cmp = va < vb ? -1 : va > vb ? 1 : 0;
                    return (sortInfo.sortOrder || 1) * cmp;
                });
            }
            setDisplayedModels(items);
        } else {
            setDisplayedModels([]);
        }
    }, [models, sortInfo.sortField, sortInfo.sortOrder]);

    const onPage = (event) => setLazyParams({ first: event.first, rows: event.rows, page: event.page + 1 });
    const onSort = (e) => setSortInfo({ sortField: e.sortField, sortOrder: e.sortOrder });

    // filtre davranışı
    const toggleFilter = () => setFilterOpen((p) => !p);

    const handleBrandChange = (val) => {
        const prevBrandShort = brandValue?.brandShort || null;
        const nextBrandShort = val?.brandShort || null;
        setBrandValue(val);
        if (prevBrandShort !== nextBrandShort) {
            setModelValue(null); // marka değişince model sıfırlansın
        }
    };

    const handleResetFilters = () => {
        setBrandValue(null);
        setProductGroupValue('');
        setModelValue(null);

        setActiveApiFilters({
            Brand: null,
            ModelName: null,
            ModelCode: null,
            BrandName: null
        });
        setSearchText('');
        setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
    };

    const handleApplyFilters = () => {
        const next = {
            Brand: brandValue?.brandShort || null, // Brand
            ModelName: modelValue?.name || null, // ModelName
            ModelCode: productGroupValue || null, // ModelCode (inputtan)
            BrandName: null
        };

        setActiveApiFilters(next);
        setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
    };

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">{th('page.modelTitle')}</h1>
            </div>

            <div className="generic-table-filter mb-3">
                <div className="grid">
                    <div className="col-12 md:col-6 flex justify-content-start">
                        <Button icon="pi pi-filter" label={th('filter.advancedFilter')} onClick={toggleFilter} outlined={!filterOpen} />
                    </div>
                </div>

                {filterOpen && (
                    <div className="mt-3">
                        <Divider />
                        <div className="text-xl font-medium mb-3">{th('filter.advancedFilters')}</div>

                        <div className="flex flex-wrap gap-2" style={{ alignItems: 'end' }}>
                            <div className="flex-auto" style={{ minWidth: '240px', maxWidth: '300px' }}>
                                <div className="field mb-2 p-1">
                                    <label className="block mb-1 text-sm">{th('table.brand')}</label>
                                    <BrandDropdown value={brandValue} onChange={handleBrandChange} placeholder={th('filter.dropdownPlaceholder', { field: th('table.brand') })} className="w-full" />
                                </div>
                            </div>

                            <div className="flex-auto" style={{ minWidth: '240px', maxWidth: '300px' }}>
                                <div className="field mb-2 p-1">
                                    <label className="block mb-1 text-sm">{th('table.model')}</label>
                                    <ModelDropdown
                                        key={`md-${brandValue?.brandShort || 'all'}`}
                                        value={modelValue}
                                        onChange={setModelValue}
                                        {...(brandValue?.brandShort ? { brand: brandValue.brandShort } : {})}
                                        placeholder={th('filter.dropdownPlaceholder', { field: th('table.model') })}
                                        className="w-full"
                                    />
                                </div>
                            </div>

                            <div className="flex-auto" style={{ minWidth: '240px', maxWidth: '300px' }}>
                                <div className="field mb-2 p-1">
                                    <label className="block mb-1 text-sm">{th('table.productGroup')}</label>
                                    <InputText value={productGroupValue} onChange={(e) => setProductGroupValue(e.target.value)} placeholder={th('filter.inputPlaceholder', { field: th('table.productGroup') })} className="w-full" />
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-content-start mt-3">
                            <Button label={th('filter.reset')} icon="pi pi-refresh" className="p-button-outlined mr-2" onClick={handleResetFilters} />
                            <Button label={th('filter.apply')} icon="pi pi-check" onClick={handleApplyFilters} />
                        </div>
                    </div>
                )}
            </div>

            <DataTable
                value={displayedModels}
                lazy
                paginator
                first={lazyParams.first}
                rows={lazyParams.rows}
                onPage={onPage}
                totalRecords={models.totalCount}
                className="p-datatable-gridlines"
                showGridlines
                dataKey="id"
                loading={loadingModels}
                emptyMessage={th('table.noModelsFound')}
                rowsPerPageOptions={[5, 10, 25, 50, 100, 500, 1000]}
                tableStyle={{ minWidth: '50rem' }}
                onSort={onSort}
                sortField={sortInfo.sortField}
                sortOrder={sortInfo.sortOrder}
                removableSort
            >
                <Column field="brand" header={th('table.brand')} sortable style={{ width: '15%' }} />
                <Column field="model" header={th('table.model')} sortable style={{ width: '20%' }} />
                <Column field="productGroup" header={th('table.productGroup')} sortable style={{ width: '15%' }} />
            </DataTable>
        </div>
    );
};

export default ModelPage;
